import React from 'react';
import { CheckCircle2, Clock, XCircle, Info } from 'lucide-react';
import './StatusBadge.css';

/**
 * Shared status pill mapped to the global standard color system.
 * Color is never the only signal: a dot + readable label text always show.
 *
 * <StatusBadge status="paid" />
 * <StatusBadge status="not_supported" label="Not Supported" />
 *
 * Enterprise additions (backward compatible):
 *   detail  — appends progress/detail text -> "Confirming · 8/12"
 *             <StatusBadge status="confirming" detail="8/12" />
 *   dot     — when true, renders the lighter dot-style pill used by the
 *             redesigned enterprise pages (still shows the label text).
 *   icon    — when true, swaps the dot for a glyph that matches the tone
 *             (check / clock / cross). The glyph is decorative: the label text
 *             still carries the meaning, so colour is never the only signal.
 */

// Tone -> glyph, for `icon` mode. Neutral tones keep the dot: there is no
// honest glyph for "cancelled" that a cross would not misread as "failed".
const TONE_ICON = {
  success: CheckCircle2,
  warning: Clock,
  danger: XCircle,
  info: Info,
};

// status (normalized) -> { tone, label }
const STATUS_MAP = {
  // green — positive / settled
  succeeded: { tone: 'success', label: 'Succeeded' },
  paid: { tone: 'success', label: 'Paid' },
  overpaid: { tone: 'success', label: 'Overpaid' },
  confirmed: { tone: 'success', label: 'Confirmed' },
  completed: { tone: 'success', label: 'Completed' },
  approved: { tone: 'success', label: 'Approved' },
  active: { tone: 'success', label: 'Active' },

  // amber — in progress / needs attention
  pending: { tone: 'warning', label: 'Pending' },
  open: { tone: 'warning', label: 'Open' },
  processing: { tone: 'warning', label: 'Processing' },
  confirming: { tone: 'warning', label: 'Confirming' },
  underpaid: { tone: 'warning', label: 'Underpaid' },
  partial: { tone: 'warning', label: 'Partial' },
  overdue: { tone: 'warning', label: 'Overdue' },
  partially_paid: { tone: 'warning', label: 'Partially Paid' },

  // red — failed / rejected
  failed: { tone: 'danger', label: 'Failed' },
  rejected: { tone: 'danger', label: 'Rejected' },
  not_supported: { tone: 'danger', label: 'Not Supported' },
  not_supported_token: { tone: 'danger', label: 'Not Supported' },
  // Payment-status form of the same condition: funds arrived in a token the
  // merchant does not accept, so the amount owed is still outstanding.
  paid_unsupported_token: { tone: 'danger', label: 'Unsupported Token' },

  // neutral-purple — cancelled / draft / refunded
  cancelled: { tone: 'neutral', label: 'Cancelled' },
  canceled: { tone: 'neutral', label: 'Cancelled' },
  refunded: { tone: 'neutral', label: 'Refunded' },
  draft: { tone: 'muted', label: 'Draft' },

  // blue — informational
  info: { tone: 'info', label: 'Info' },
};

const StatusBadge = ({ status, label, detail, dot = false, icon = false, className = '' }) => {
  const key = String(status ?? '').trim().toLowerCase();
  const mapped = STATUS_MAP[key];

  const tone = mapped ? mapped.tone : 'muted';
  const baseText =
    label ??
    (mapped
      ? mapped.label
      : key
        ? key.replace(/_/g, ' ')
        : 'Unknown');

  const text = detail ? `${baseText} · ${detail}` : baseText;
  const Glyph = icon ? TONE_ICON[tone] : null;

  return (
    <span
      className={`ui-status-badge ui-status-badge--${tone}${dot ? ' ui-status-badge--dot' : ''}${
        Glyph ? ' ui-status-badge--icon' : ''
      } ${className}`.trim()}
      role="status"
    >
      {Glyph ? (
        <Glyph className="ui-status-badge__glyph" size={13} aria-hidden="true" />
      ) : (
        <span className="ui-status-badge__dot" aria-hidden="true" />
      )}
      {text}
    </span>
  );
};

export default StatusBadge;
